import {
  computed,
  inject,
  Injectable,
  Signal,
  signal,
  WritableSignal
} from '@angular/core';
import { TokenUuid } from '../../../core/api/models/token-uuid';
import { PoliciesMode } from '../../helpers/client.utils';
import { Observable } from 'rxjs';
import { TokenResponse, ValidateStepResponse } from '../http/dto';
import { IAuthHttpService } from '../http/auth/auth-http-service.interface';
import { AUTH_HTTP_SERVICE } from '../http/http-service.token';
import { AcsMode } from './project.interface';
import { getFingerprint } from '../../helpers/fingerprint';

export interface VisitorParamsModel {
  broker_id: string;
  visitor_token: string;
}

export interface QueryParamMapModel {
  token?: string | null;
  uuid: string | null;
  producerCode: string | null;
  mode: string | null;
  entity: string | null;
}

export interface PayloadABCToken {
  sub: string;
  broker_id: string;
  broker_name: string;
  visitor_id: string;
}

export interface PayloadModulrToken {
  sub: string;
  noCrm: boolean;
  origin: AcsMode;
  uuid: string;
  entity: AuthEntity;
  nbf: number;
  exp: number;
  iat: number;
}

export interface AuthStateInterface {
  token?: string | null;
  uuid: string | null;
  producerCode: string | null;
  mode: string | null;
  entity: AuthEntity | null;
  token_producer?: string | null;
  referrer: AuthReferrer | null;
}

export type AuthEntity = 'clients' | 'policies';
export type AuthReferrer = 'clients' | 'producers';
export type AuthInitializationStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE';

@Injectable({
  providedIn: 'root'
})
export class AuthStateService {
  private data: AuthStateInterface = {
    entity: null,
    mode: null,
    token: null,
    producerCode: null,
    uuid: null,
    token_producer: null,
    referrer: null
  };

  readonly keyUrlParamInfo = 'paramInfo';
  readonly keyUuidToken: string = 'uuidToken';
  readonly visitorParamsKey: string = 'visitorParams';
  readonly keyToken: string = 'token';
  readonly keyIsPublic: string = 'public';
  readonly haveToAuth = signal(true);
  readonly authHttp = inject<IAuthHttpService>(AUTH_HTTP_SERVICE);
  private initStatus: AuthInitializationStatus = 'NOT_STARTED';
  private initStatusState: WritableSignal<AuthInitializationStatus> = signal(
    this.initStatus
  );
  private dataState: WritableSignal<AuthStateInterface> = signal(this.data);

  private readonly _isPublic = signal<boolean>(
    sessionStorage.getItem(this.keyIsPublic) === 'true'
  );

  readonly isPublic = this._isPublic.asReadonly();

  constructor() {
    this.initData();
  }

  public updateData(data: Partial<AuthStateInterface>) {
    this.dataState.update((state) => ({ ...state, ...data }));
    sessionStorage.setItem(
      this.keyUrlParamInfo,
      JSON.stringify({ ...this.dataState(), ...data })
    );
  }

  public updateInitStatus(status: AuthInitializationStatus) {
    this.initStatus = status;
    this.initStatusState.set(this.initStatus);
  }

  public updateIsPublic(value: boolean) {
    this._isPublic.set(value);
    sessionStorage.setItem(this.keyIsPublic, value ? 'true' : 'false');
  }

  public updateHaveToAuth(value: boolean) {
    this.haveToAuth.set(value);
    sessionStorage.setItem('haveToAuth', value ? 'true' : 'false');
  }

  public selectInitStatusState() {
    return this.initStatusState;
  }

  public saveUuidToken(token: TokenUuid): void {
    sessionStorage.setItem(this.keyUuidToken, JSON.stringify(token));
    const paramInfo = this.getSessionStorageUrlParamInfo()!;
    paramInfo.token = token.token;
    paramInfo.uuid = token.uuid;
    sessionStorage.setItem(this.keyUrlParamInfo, JSON.stringify(paramInfo));
  }

  public selectAuthState() {
    return this.dataState;
  }

  public selectToken(): Signal<string> {
    const data = JSON.parse(sessionStorage.getItem(this.keyUrlParamInfo)!);
    return computed(() => (this.dataState().token ?? data ? data.token : null));
  }

  public selectUuid(): Signal<string> {
    return computed(
      () =>
        this.dataState().uuid ??
        JSON.parse(sessionStorage.getItem(this.keyUuidToken)!).uuid
    );
  }

  public getSessionStorageUrlParamInfo(): AuthStateInterface | null {
    const param = sessionStorage.getItem(this.keyUrlParamInfo);
    return param ? JSON.parse(param) : null;
  }

  public selectProducerCode(): Signal<string | null> {
    return computed(() => this.dataState().producerCode);
  }

  public selectAcsMode(): Signal<
    'expat_internal' | 'expat_external_client' | 'expat_external_producer'
  > {
    return computed(
      () =>
        this.dataState().mode ??
        JSON.parse(sessionStorage.getItem(this.keyUrlParamInfo)!).mode!
    );
  }

  public setVisitorParams(
    broker_id: string,
    token: string,
    newToken: string
  ): void {
    const data: VisitorParamsModel = {
      broker_id: broker_id,
      visitor_token: token
    };
    this.updateData({
      uuid: null,
      token: null,
      mode: 'expat_external_producer',
      producerCode: broker_id,
      entity: PoliciesMode.CLIENTS,
      token_producer: newToken
    });
    sessionStorage.setItem(this.visitorParamsKey, JSON.stringify(data));
  }

  public getVisitorParam(): VisitorParamsModel | null {
    const data = sessionStorage.getItem(this.visitorParamsKey);
    return data ? JSON.parse(data) : null;
  }

  public setToken(token: string) {
    sessionStorage.setItem(this.keyToken, token);
  }

  public verifyToken(token: string) {
    return this.authHttp.postExchangeToken({
      visitor_token: token,
      fingerprint: getFingerprint()
    });
  }

  public getAccessCode(uuid: string, code: string): Observable<ValidateStepResponse> {
    return this.authHttp.postSendVerificationCode({
      uuid: uuid,
      code: code
    });
  }

  public requestAccessToken(uuid: string, code: string): Observable<ValidateStepResponse> {
    return this.authHttp.postSendVerificationCode({
      uuid: uuid,
      code: code
    });
  }

  public reset() {
    this.dataState.update((value) => ({
      ...value,
      token: null,
      uuid: null,
      referrer: null,
      mode: null,
      entity: null
    }));
    sessionStorage.setItem(
      this.keyUrlParamInfo,
      JSON.stringify(this.dataState())
    );
  }

  public isModulrToken(
    payload: PayloadABCToken | PayloadModulrToken
  ): payload is PayloadModulrToken {
    return 'iss' in payload && payload.iss === 'modulr';
  }

  private initData() {
    const data = sessionStorage.getItem(this.keyUrlParamInfo);
    this.updateIsPublic(sessionStorage.getItem('public') === 'true');
    if (data) {
      this.updateData(JSON.parse(data));
    }
  }
}
